import { useEffect, useMemo, useState } from "react";
import { NavButtons } from "../components/NavButtons";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getCached, setCached } from "@/lib/cache";
import { toast } from "sonner";
import FilterPills from "@/components/FilterPills";
import { ArrowLeft, Calendar, ArrowsClockwise } from "@phosphor-icons/react";

const parseDay = (s) => {
  if (!s) return null;
  const iso = String(s).slice(0, 10);
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const fmtDate = (d) => {
  const p = parseDay(d);
  if (!p) return "—";
  return p.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

function EtaBadge({ eta }) {
  const d = parseDay(eta);
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  let bg = "#e5e7eb", color = "#374151", label = `In ${days}d`;
  if (days < 0) { bg = "#fee2e2"; color = "#991b1b"; label = `Overdue by ${-days}d`; }
  else if (days === 0) { bg = "#fef3c7"; color = "#78350f"; label = "Due today"; }
  else if (days <= 7) { bg = "#fef3c7"; color = "#78350f"; label = `${days}d to go`; }
  else if (days <= 30) { bg = "#dbeafe"; color = "#1e3a8a"; label = `${days}d to go`; }
  else { label = `${days}d to go`; }
  return <span className="label px-2 py-0.5" style={{ background: bg, color, border: `1px solid ${bg}` }}>{label}</span>;
}

export default function AdminProjects() {
  const [clients, setClients] = useState(() => getCached("admin-projects") ?? []);
  const [q, setQ] = useState("");
  const [range, setRange] = useState("all"); // "7" | "14" | "month" | "all"
  const load = async () => {
    try {
      const { data } = await api.get("/admin/clients");
      setClients(data);
      setCached("admin-projects", data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const projects = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const term = q.trim().toLowerCase();
    return clients
      .filter(c => !c.archived && c.estimated_completion_date)
      .filter(c => !term || [c.name, c.business_name, c.email]
        .some(v => v && String(v).toLowerCase().includes(term)))
      .filter(c => {
        if (range === "all") return true;
        const d = parseDay(c.estimated_completion_date);
        if (!d) return false;
        const days = Math.round((d - today) / 86400000);
        if (range === "7") return days >= 0 && days <= 7;
        if (range === "14") return days >= 0 && days <= 14;
        if (range === "month") return d >= today && d <= endOfMonth;
        return true;
      })
      .sort((a, b) => (a.estimated_completion_date || "").localeCompare(b.estimated_completion_date || ""));
  }, [clients, q, range]);

  // Counts for the filter pills (independent of the current filter, but honor search)
  const rangeCounts = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const term = q.trim().toLowerCase();
    const base = clients
      .filter(c => !c.archived && c.estimated_completion_date)
      .filter(c => !term || [c.name, c.business_name, c.email]
        .some(v => v && String(v).toLowerCase().includes(term)));
    const within = (min, max) => base.filter(c => {
      const d = parseDay(c.estimated_completion_date);
      if (!d) return false;
      const days = Math.round((d - today) / 86400000);
      return days >= min && days <= max;
    }).length;
    const inMonth = base.filter(c => {
      const d = parseDay(c.estimated_completion_date);
      return d && d >= today && d <= endOfMonth;
    }).length;
    return { seven: within(0, 7), fourteen: within(0, 14), month: inMonth, all: base.length };
  }, [clients, q]);

  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="admin-projects">
      <header className="flex items-center justify-between px-4 md:px-6 py-6 border-b gap-3" style={{ borderColor: "var(--hair)" }}>
        <div className="flex items-center gap-3 min-w-0">
          <NavButtons home="/admin" testid="projects-back" />
          <div className="min-w-0">
            <p className="label">Console</p>
            <h1 className="title-xl text-2xl md:text-4xl mt-1 truncate">Projects.</h1>
          </div>
        </div>
        <button data-testid="projects-refresh" onClick={load} className="btn-ghost text-xs shrink-0">
          <ArrowsClockwise size={14} weight="bold" /> <span className="hidden sm:inline">Refresh</span>
        </button>
      </header>

      <section className="p-4 md:p-6 space-y-6">
        <input
          type="search"
          data-testid="projects-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search projects by name, business or email…"
          className="w-full px-4 py-2.5 text-sm"
          autoComplete="off"
        />

        <FilterPills
          testid="projects-range"
          value={range}
          onChange={setRange}
          options={[
            { k: "7", label: `Due within 7 days (${rangeCounts.seven})` },
            { k: "14", label: `Due within 14 days (${rangeCounts.fourteen})` },
            { k: "month", label: `Due this month (${rangeCounts.month})` },
            { k: "all", label: `All (${rangeCounts.all})` },
          ]}
        />

        <div className="panel p-2 md:p-4">
          {projects.length === 0 ? (
            <p className="label py-8 text-center">No projects in this range</p>
          ) : (
            <div className="space-y-2">
              {projects.map(c => (
                <Link key={c.user_id}
                  to={`/admin/clients/${c.user_id}`}
                  data-testid={`project-row-${c.user_id}`}
                  className="hair p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3 hover:bg-black/5 transition-colors rounded no-underline"
                  style={{ color: "inherit" }}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-medium">{c.business_name || c.name}</span>
                      <span className="label" style={{ overflowWrap: "anywhere" }}>{c.email}</span>
                    </div>
                    <p className="label mt-1">
                      Due by client: {c.due_by_client || 0} · Due by agency: {c.due_by_agency || 0}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <p className="text-sm flex items-center gap-1 justify-end">
                        <Calendar size={12} /> {fmtDate(c.estimated_completion_date)}
                      </p>
                      <div className="mt-1"><EtaBadge eta={c.estimated_completion_date} /></div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
